/**
 * Makes the Canvas App's React reconciler tolerant of third-party DOM mutations
 * that relocate a React-managed child — specifically GSAP ScrollTrigger's `pin`,
 * which wraps a pinned element in a `.pin-spacer` div (`parent > E` becomes
 * `parent > spacer > E`) and un-wraps it again on `kill()`/rebuild.
 *
 * The Canvas App portals React into `document.documentElement`, so React
 * reconciles the `<body>` subtree. When GSAP externally wraps a child, React's
 * fiber still lists that child as a direct child of its original parent; the next
 * reconcile then calls `removeChild`/`insertBefore` against a node that now lives
 * inside the spacer, throwing a DOM `NotFoundError` and crashing the canvas. (This
 * is the same failure class as the well-known "Google Translate breaks React" bug.)
 *
 * The patch keeps native behavior on the fast path (child is where React expects
 * it) and only redirects when a child has been relocated — walking up to the
 * spacer that now occupies the child's original slot. Redirecting (rather than the
 * classic no-op) keeps the live DOM consistent with React's intent: removals
 * actually detach, insertions land in the right position, so fiber and DOM stay in
 * agreement.
 *
 * This lives only in the Canvas iframe bundle. The iframe is an isolated realm
 * (its own `window`/`Node`), so patching `Node.prototype` here cannot leak to the
 * top Designer frame or any other origin. Do not import this file into a
 * non-canvas bundle. Published sites run the vanilla IX3 runtime with no React and
 * never need this.
 */

/**
 * Returns the ancestor of `node` whose `parentNode` is `parent` — i.e. the
 * pin-spacer that now occupies `node`'s original slot in `parent`'s child list —
 * or `null` if `node` is not currently a descendant of `parent`.
 */
function slotChildOf(parent: Node, node: Node): Node | null {
  let current: Node | null = node;
  while (current && current.parentNode !== parent) {
    current = current.parentNode;
  }
  return current;
}

/** GSAP's default `spacerClass` for the wrapper a pin inserts. */
function isPinSpacer(node: Node): boolean {
  return node instanceof Element && node.classList.contains('pin-spacer');
}

let installed = false;

export function patchReactPinResilience(): void {
  if (installed) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;
  const originalAppendChild = Node.prototype.appendChild;

  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T
  ): T {
    // Fast path: child is where React expects it — native semantics.
    if (child.parentNode === this) {
      return originalRemoveChild.call(this, child) as T;
    }
    // child was relocated into a pin-spacer under `this`. Removing the spacer
    // detaches the spacer *and* the child it wraps, leaving `this`'s direct-child
    // list matching what React's fiber believes it is after removing `child`.
    const slot = slotChildOf(this, child);
    if (slot && slot !== child && isPinSpacer(slot)) {
      originalRemoveChild.call(this, slot);
      return child;
    }
    // child sits under some other wrapper, not a pin-spacer. Detaching that
    // ancestor would silently tear down a subtree React never asked to remove,
    // so keep the native NotFoundError and let a real reconciler/DOM mismatch
    // stay loud.
    if (slot) {
      return originalRemoveChild.call(this, child) as T;
    }
    // child is not under `this` at all. If it has no parent, React's intent —
    // that `child` be gone — is already satisfied, so no-op rather than throw. If
    // it still has a parent (a live one, or a detached wrapper or fragment that
    // could be reattached), something moved it there and reporting success would
    // leave it alive with no fiber; keep the native NotFoundError so that
    // mismatch stays loud, like the case above.
    if (child.parentNode === null) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    refChild: Node | null
  ): T {
    // React is reordering a child that GSAP wrapped: `newNode` sits inside a
    // pin-spacer that is a direct child of `this`. A native move would pull it
    // out of the spacer and strand an empty spacer, so move the spacer as the
    // unit. The reference is resolved to its own slot the same way as below.
    if (newNode.parentNode !== null && newNode.parentNode !== this) {
      const newSlot = slotChildOf(this, newNode);
      if (newSlot && newSlot !== newNode && isPinSpacer(newSlot)) {
        // GSAP unwrapping its own pin: `parent.insertBefore(pin, spacer)` must
        // really move `pin` OUT of the spacer. Treating it as a spacer move
        // inserts the spacer before itself (a no-op), and the
        // `removeChild(spacer)` that follows would detach the pinned element
        // along with it.
        if (refChild === newSlot) {
          return originalInsertBefore.call(this, newNode, refChild) as T;
        }
        let refSlot: Node | null = refChild;
        if (refChild !== null && refChild.parentNode !== this) {
          const found = slotChildOf(this, refChild);
          // A reference under a non-pin wrapper (or outside `this`) is a real
          // mismatch: keep the native error rather than guess a position.
          if (!found || !isPinSpacer(found)) {
            return originalInsertBefore.call(this, newNode, refChild) as T;
          }
          refSlot = found;
        }
        originalInsertBefore.call(this, newSlot, refSlot);
        return newNode;
      }
    }
    // Fast path: no reference node, or the reference is where React expects it.
    if (refChild === null || refChild.parentNode === this) {
      return originalInsertBefore.call(this, newNode, refChild) as T;
    }
    // refChild was relocated into a pin-spacer under `this`. The spacer sits
    // exactly where refChild used to be, so inserting before the spacer preserves
    // React's intended sibling order.
    const slot = slotChildOf(this, refChild);
    if (slot && isPinSpacer(slot)) {
      return originalInsertBefore.call(this, newNode, slot) as T;
    }
    // refChild sits under some other wrapper, or is not under `this` at all.
    // Neither is a pin wrap, so keep the native NotFoundError, as `removeChild`
    // does, and let a real reconciler/DOM mismatch stay loud.
    return originalInsertBefore.call(this, newNode, refChild) as T;
  } as typeof Node.prototype.insertBefore;

  // React places a child at the end of its parent with `appendChild`, not
  // `insertBefore(node, null)`, so moving a pinned child to the end needs the
  // same move-the-spacer-as-a-unit handling. A detached `newNode` (a fresh
  // insertion) and one already under `this` skip the ancestor walk.
  Node.prototype.appendChild = function <T extends Node>(
    this: Node,
    newNode: T
  ): T {
    if (newNode.parentNode !== null && newNode.parentNode !== this) {
      const slot = slotChildOf(this, newNode);
      if (slot && slot !== newNode && isPinSpacer(slot)) {
        originalAppendChild.call(this, slot);
        return newNode;
      }
    }
    return originalAppendChild.call(this, newNode) as T;
  } as typeof Node.prototype.appendChild;
}
